import { Divider, Button, Text, Group, UnstyledButton, Paper } from "@mantine/core";
import { DatePicker, DatesProvider } from "@mantine/dates";
import { useState, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { dateFnsLocales } from "../../../../constants/dateFnsLocales.js";
import { format } from "date-fns";
import type { DateRangeSelectorProps } from "./DateRangeSelector.js";
import { IconSquareX } from "@tabler/icons-react";
import type { DateRange } from "../constant/dateRange.js";

interface DatePickerStackProps extends DateRangeSelectorProps {
  closeDatePicker: () => void;
};
type TemporalDateRange = [Date | null, Date | null];

export const DatePickerSection: FC<DatePickerStackProps> = ({
  dateRange,
  setDateRange,
  closeDatePicker,
}) => {
  const { locale } = useIntl();
  const [temporalDateRange, setTemporalDateRange] = useState<TemporalDateRange>(dateRange);

  return (
    <Paper>
      <Group p="md" justify="space-between">
        <Text fz="sm" fw={600}>
          <FormattedMessage defaultMessage="Select a date range" id="dateModal.header" />
        </Text>
        <UnstyledButton onClick={closeDatePicker}>
          <IconSquareX />
        </UnstyledButton>
      </Group>

      <Divider />

      <DatesProvider settings={{ locale }}>
        <DatePicker
          p="md"
          type="range"
          value={temporalDateRange}
          onChange={([stringifiedStartDate, stringifiedEndDate]) => {
            const dateTuple: TemporalDateRange = [
              stringifiedStartDate ? new Date(stringifiedStartDate) : null,
              stringifiedEndDate ? new Date(stringifiedEndDate) : null,
            ];
            setTemporalDateRange(dateTuple);
          }}
          numberOfColumns={2}
          columnsToScroll={1}
        />
      </DatesProvider>

      <Divider />

      {(temporalDateRange[0] && temporalDateRange[1]) && (
        <Group p="md">
          <Text fz="sm">
            <FormattedMessage
              defaultMessage="Selected period:"
              id="dateModal.selectedPeriod"
            />
            {" "}
            <Text component="span" fw={600}>
              <FormattedMessage
                defaultMessage="{fromDate} - {toDate}"
                id="dateRangeSelector.selectedPeriod"
                values={{
                  fromDate: format(temporalDateRange[0], "P", {
                    locale: dateFnsLocales[locale],
                  }),
                  toDate: format(temporalDateRange[1], "P", {
                    locale: dateFnsLocales[locale],
                  }),
                }}
              />
            </Text>
          </Text>
          <Button
            onClick={() => {
              setDateRange(temporalDateRange as DateRange);
              closeDatePicker();
            }}
            variant="primary"
            color="black"
            className="grow"
          >
            <FormattedMessage
              defaultMessage="Apply"
              id="words.apply"
            />
          </Button>
        </Group>
      )}
    </Paper>
  );
};
